import { useRef } from "react";

import { VoteStatus } from "../Deliberation/useVoteState";
import { getDeliberationOptionColor } from "./services/getDeliberationOptionColor";

import BasicCaseProposal from "./Buttons/BasicCaseProposal";
import { StyledProposals, Wrapper } from "./VoteBasic.styles";
import { useBoxPosition } from "./useBoxPosition";

type Props = {
    propositions: Array<{
        disableOtherPropositionsIfSelected: boolean;
        id: string;
        shortText: string;
        isVisibleByDefault: boolean;
    }>;
    answers: Array<{
        id: string;
        weight: number;
        disableOtherPropositionsIfSelected: boolean;
    }>;
    isSecret: boolean;
    voteStatus: string;
    replaceProposal: (propositionId: string) => void;
    proposalsOrientation: string;
    voterName: string;
    titleId: string;
    // Set when the user votes for several voters, to name each group after its voter.
    groupVoterName?: string;
};

const VoteBasic = ({
    propositions,
    answers,
    isSecret,
    voteStatus,
    replaceProposal,
    proposalsOrientation,
    voterName,
    titleId,
    groupVoterName,
}: Props) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const isCompact = "horizontal" === proposalsOrientation;
    const boxPosition = useBoxPosition({
        containerRef: wrapperRef,
        isSideBySide: isCompact,
        optionCount: propositions.length,
    });

    const renderProposals = () => {
        return propositions.map((proposition, item) => {
            const isActive =
                VoteStatus.Success === voteStatus && isSecret
                    ? false
                    : answers.some(
                          (answer) =>
                              answer.id === proposition.id && answer.weight > 0,
                      );

            const isDisabled = () => {
                if (VoteStatus.Success === voteStatus) {
                    return true;
                }

                if (isActive) {
                    return false;
                }

                return false;
            };

            const handleProposalClick = () => {
                replaceProposal(proposition.id);
            };

            return (
                <BasicCaseProposal
                    text={proposition.shortText}
                    isActive={isActive}
                    isCompact={isCompact}
                    isDisabled={isDisabled()}
                    boxPosition={boxPosition}
                    handleClick={handleProposalClick}
                    key={item}
                    color={getDeliberationOptionColor(proposition.id)}
                />
            );
        });
    };

    return (
        <Wrapper dir="ltr" ref={wrapperRef}>
            {/* In case of proxies, the position of proposals is horizontal */}
            <StyledProposals
                $proposalsOrientation={proposalsOrientation}
                data-testid={`voter-${voterName}`}
                titleId={titleId}
                voterName={groupVoterName}
            >
                {renderProposals()}
            </StyledProposals>
        </Wrapper>
    );
};

export default VoteBasic;
